import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, Users, Banknote, ShieldCheck, Wallet, TrendingUp } from "lucide-react";
import { useApp } from "@/App";
import { useTranslation } from "react-i18next";
import { useRubTopupAvailability } from "@/hooks/useRubTopupAvailability";

const ServicesPage = () => {
  const rubTopupEnabled = useRubTopupAvailability();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { haptic } = useApp();
  const soonTextRaw = t('topup.soon') || 'Скоро';
  const soonText = soonTextRaw ? soonTextRaw.charAt(0).toUpperCase() + soonTextRaw.slice(1) : 'Скоро';
  const isRu = i18n.language?.toLowerCase().startsWith("ru");

  const services = [
    { id: "foreign-card", icon: CreditCard, labelKey: "services.foreign_card", descKey: "services.foreign_card_desc", path: "/cards" },
    {
      id: "rub-topup",
      icon: Wallet,
      labelKey: "services.rub_topup",
      descKey: "services.rub_topup_desc",
      path: "/rub-topup",
      labelOverride: isRu ? "Пополнить рублём" : "Top up with RUB",
      descOverride: isRu ? "Купить крипту за рубли" : "Buy crypto for rubles",
    },
    {
      id: "cash-withdrawal",
      icon: Banknote,
      labelKey: "services.cards",
      descKey: "services.cards_desc",
      path: "/cash-out",
      labelOverride: isRu ? "Выдача наличных" : "Cash Withdrawal",
      descOverride: isRu ? "Наличный обмен криптовалюты" : "Cash crypto exchange",
    },
    { id: "staking", icon: TrendingUp, labelKey: "services.staking", descKey: "services.staking_desc", path: "/staking",
      labelOverride: isRu ? "Стейкинг" : "Staking", descOverride: isRu ? "Проценты на заблокированные USDT" : "Earn on locked USDT" },
    { id: "referral", icon: Users, labelKey: "services.referral", descKey: "services.referral_desc", path: "/referral" },
    {
      id: "aml-check",
      icon: ShieldCheck,
      labelKey: "services.aml",
      descKey: "services.aml_desc",
      path: "/aml-check",
      labelOverride: isRu ? "AML-проверка" : "AML Check",
      descOverride: isRu ? "Проверка кошелька на риски" : "Wallet risk screening",
    },
  ];

  const handleService = (service) => {
    haptic('light');
    if (service.path) {
      navigate(service.path, { state: { from: location.pathname + location.search } });
    }
  };

  return (
    <div className="px-4 pb-4 pt-[calc(1rem+var(--lp-top))] safe-bottom animate-fade-in" data-testid="services-page">
      <h1 className="mb-6 text-[26px] font-bold tracking-[-0.03em]">{t('services.title')}</h1>

      <div className="grid grid-cols-2 gap-4">
        {services.filter((service) => service.id !== "rub-topup" || rubTopupEnabled).map((service) => {
          const Icon = service.icon;
          const isComingSoon = !service.path;
          return (
            <button
              key={service.id}
              onClick={() => handleService(service)}
              disabled={isComingSoon}
              className={`lp-glass p-4 text-left ${
                isComingSoon ? "cursor-not-allowed opacity-70" : "lp-press"
              }`}
              data-testid={`service-${service.labelKey}`}
            >
              {isComingSoon && (
                <span className="lp-tile absolute right-3 top-3 rounded-full px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {soonText}
                </span>
              )}
              {service.id === "foreign-card" && (
                <div data-testid="foreign-cards-maintenance-badge" className="mb-3 rounded-lg px-2 py-2 text-xs font-bold" style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #f59e0b" }}>
                  {t("cards.maintenance_unavailable")}
                </div>
              )}
              {/* Иконка сидит в такой же плитке, как везде: раньше это был
                  зашитый bg-blue-50 — единственная поверхность в приложении,
                  не менявшаяся вместе с темой. */}
              <div className="lp-tile mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                <Icon className="h-6 w-6 text-link" />
              </div>
              <h3 className="font-semibold text-sm text-foreground">
                {service.labelOverride || t(service.labelKey)}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {service.descOverride || t(service.descKey)}
              </p>
            </button>
          );
        })}
      </div>
      {/* Extra space for the bottom QR button */}
      <div className="h-5" />
    </div>
  );
};

export default ServicesPage;
